import { Controller, type FieldPath, type FieldValues } from 'react-hook-form';
import { FloatField, type FloatFieldInfo, type FloatFieldOption } from './FloatField';

export interface FormFloatFieldProps<TFieldValues extends FieldValues> {
  /** Field path; the component reads `control` from the surrounding `FormProvider`. */
  name: FieldPath<TFieldValues>;
  label: string;
  /** Control rendered inside the box. @default 'text' */
  kind?: 'text' | 'select' | 'date' | 'textarea';
  /** Options for `kind="select"` — strings or `{ value, label }`. */
  options?: FloatFieldOption[];
  /** Static prefix shown before the value once floated (e.g. "$"). */
  prefix?: string;
  /** Optional info tooltip (text fields only). */
  info?: FloatFieldInfo;
  inputMode?: 'numeric' | 'text';
  /** Format hint, shown only while the field is focused and empty (e.g. "(000) 000-0000"). */
  placeholder?: string;
  /** Hard character cap enforced by the control itself. */
  maxLength?: number;
  /** Resting height in text rows — `kind="textarea"` only. */
  rows?: number;
  disabled?: boolean;
  /**
   * Marks the field as holding sensitive data (e.g. card number, CVV, bank account). Disables
   * browser autofill/autocorrect and signals password managers (1Password, LastPass) to skip it,
   * so the value is never captured, suggested, or stored. Use for any PCI/PII input.
   */
  sensitive?: boolean;
  /** Transform raw input (formatting / digit-stripping) before it reaches form state. */
  format?: (value: string) => string;
  /**
   * Resolve a validation message to display text. Schemas commonly store i18n keys as the
   * message; pass an app-scoped translator (e.g. a namespaced `t`). Defaults to identity, so a
   * schema that stores already-display-ready messages needs no translator.
   */
  translateError?: (message: string) => string;
  /** md = 48px, lg = 56px. @default 'md' */
  size?: 'md' | 'lg';
}

/**
 * Generic Controller-wrapped {@link FloatField} — the floating-label counterpart to
 * {@link FormTextField}/{@link FormSelectField}. App- and i18n-namespace-agnostic: pair it with
 * react-hook-form's `FormProvider` and (optionally) pass a `translateError` to localize messages.
 */
export function FormFloatField<TFieldValues extends FieldValues>({
  name,
  label,
  kind = 'text',
  options,
  prefix,
  info,
  inputMode = 'text',
  placeholder,
  maxLength,
  rows,
  disabled,
  sensitive,
  format,
  translateError,
  size,
}: FormFloatFieldProps<TFieldValues>) {
  // No-capture attributes for PCI/PII fields: suppress browser autofill/autocorrect and the
  // 1Password / LastPass overlays so the raw value is never persisted outside form memory.
  const sensitiveProps = sensitive
    ? ({
        autoComplete: 'off',
        autoCapitalize: 'off',
        autoCorrect: 'off',
        spellCheck: false,
        'data-1p-ignore': true,
        'data-lpignore': 'true',
      } as const)
    : undefined;

  return (
    <Controller<TFieldValues>
      name={name}
      render={({ field, fieldState }) => (
        <FloatField
          ref={field.ref} // registers the DOM element with RHF so shouldFocusError works
          id={name}
          kind={kind}
          label={label}
          options={options}
          prefix={prefix}
          info={info}
          size={size}
          disabled={disabled}
          error={fieldState.invalid}
          hint={
            fieldState.error?.message &&
            (translateError ? translateError(fieldState.error.message) : fieldState.error.message)
          }
          value={typeof field.value === 'string' ? field.value : ''}
          onBlur={field.onBlur}
          onChange={(event) => field.onChange(format ? format(event.target.value) : event.target.value)}
          inputProps={kind === 'select' ? undefined : { inputMode, placeholder, maxLength, rows, ...sensitiveProps }}
        />
      )}
    />
  );
}
